import React from 'react';
import clsx from 'clsx';

import { Button, ProgressBar, Slug } from '@components';
import type { NotificationResponse } from '@api';
import {
  getNotificationJobId,
  getNotificationProgress,
  getNotificationStatusColor,
  getRelativeTime,
} from './utils';
import type { NotificationItemType } from './types';

export type NotificationItemProps = {
  notification: NotificationResponse;
  type: NotificationItemType;
  onShowFailures?: (jobId: string) => void;
};

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  type,
  onShowFailures,
}) => {
  const jobId = getNotificationJobId(notification);
  const canShowFailures =
    type === 'regular' && Boolean(jobId) && Boolean(notification.failed_count);

  return (
    <article
      className={clsx(
        type === 'regular'
          ? 'border-b border-border pb-md last:border-b-0'
          : 'flex flex-col gap-xs rounded-xs bg-bg p-sm'
      )}
    >
      <div className="flex items-start justify-between gap-sm">
        <div
          className={clsx(
            'min-w-0 font-semibold',
            type === 'small' ? 'truncate text-tiny' : ''
          )}
        >
          {notification.title}
        </div>
        <Slug
          text={notification.status.replaceAll('_', ' ')}
          color={getNotificationStatusColor(notification.status)}
          className="shrink-0"
        />
      </div>
      {type === 'regular' && (
        <div className="mt-xs text-small opacity-80">
          {notification.message}
        </div>
      )}
      {notification.total_count != null && (
        <div
          className={clsx(
            'flex flex-col gap-xs',
            type === 'regular' ? 'mt-sm' : ''
          )}
        >
          <ProgressBar progress={getNotificationProgress(notification)} />
          <div
            className={
              type === 'regular' ? 'text-tiny' : 'text-tiny text-textLight'
            }
          >
            {notification.processed_count ?? 0}/{notification.total_count}
            {type === 'regular' ? ' processed' : ''}
            {notification.failed_count ? (
              <span className="text-danger">
                , {notification.failed_count} failed
              </span>
            ) : null}
          </div>
        </div>
      )}
      {type === 'regular' && (
        <div className="mt-sm flex items-center justify-between gap-sm text-tiny opacity-60">
          <span>{notification.actor_label || 'System'}</span>
          <span>{getRelativeTime(notification.created_at)}</span>
        </div>
      )}
      {canShowFailures && jobId && (
        <Button
          size="xs"
          type="ghost"
          icon="eye"
          className="mt-sm"
          onClick={() => onShowFailures?.(jobId)}
        >
          View {notification.failed_count} failed contact(s)
        </Button>
      )}
    </article>
  );
};

export default React.memo(NotificationItem) as typeof NotificationItem;
